export type TextItem = { title: string; body: string };

export const PRIVACY_URL = "https://studiowebvip.com/privacy-policy/";
export const TERMS_URL = "https://studiowebvip.com/terms-and-conditions/";

export type TimelineEvent = {
  label: string;
  detail?: string;
  tone: "done" | "alert" | "success";
};

export const leadTimeline: TimelineEvent[] = [
  { label: "Nuevo lead", detail: "Formulario web", tone: "done" },
  { label: "Respuesta automática enviada", detail: "00:04", tone: "done" },
  { label: "Asignado a recepción", tone: "done" },
  {
    label: "Sin respuesta del equipo",
    detail: "10 min → alerta al responsable",
    tone: "alert",
  },
  { label: "Escalamiento", detail: "30 min → alerta al dueño", tone: "alert" },
  { label: "Seguimiento automático activado", tone: "done" },
  { label: "Cita agendada", tone: "success" },
];

export const pipelineStats: string[] = ["Leads nuevos", "Respondidos", "Citas", "Sin seguimiento"];

export const problemQuestions: string[] = [
  "¿Lo llamaron?",
  "¿Contestó?",
  "¿Se le dio seguimiento?",
  "¿Agendó?",
  "¿Compró?",
  "¿O simplemente se perdió?",
];

export const problems: TextItem[] = [
  {
    title: "Responden tarde",
    body: "El cliente escribió con intención. Si pasan 30 minutos, probablemente ya habló con otro negocio.",
  },
  {
    title: "No hay seguimiento",
    body: "Muchos prospectos no compran en el primer contacto. Si nadie vuelve a escribir, se enfrían.",
  },
  {
    title: "Nadie sabe qué pasó",
    body: "Pagas anuncios, pero no ves cuántos leads fueron atendidos, cuántos agendaron y cuántos se perdieron.",
  },
  {
    title: "Todo se vuelve excusa",
    body: "“Ese lead no servía.” “Le escribí.” “No contestó.” Sin sistema, todo es opinión. Con pipeline, cada lead tiene historial, responsable y próximo paso.",
  },
];

export const steps: TextItem[] = [
  {
    title: "Llamada de 15 minutos",
    body: "Entendemos tu negocio y de dónde vienen tus leads.",
  },
  {
    title: "Visita a tu negocio",
    body: "Vemos en persona cómo entran tus mensajes, quién los atiende y dónde se quedan parados.",
  },
  {
    title: "Instalación del sistema",
    body: "Respuesta inmediata, pipeline, alertas, seguimiento automático y tablero de control.",
  },
  {
    title: "Tu equipo sabe qué hacer",
    body: "Cada lead queda asignado, medido y con próximo paso. Ya no depende de la memoria de nadie.",
  },
];

export const included: TextItem[] = [
  { title: "Respuesta inmediata", body: "Cada lead recibe respuesta en segundos, 24/7." },
  {
    title: "Pipeline claro",
    body: "Nuevo, contactado, seguimiento, cita, venta, perdido.",
  },
  {
    title: "Alertas al equipo",
    body: "10 min al responsable, 30 min al dueño.",
  },
  {
    title: "Seguimiento automático",
    body: "Si el prospecto no contesta, el sistema vuelve a escribirle.",
  },
  {
    title: "Reactivación de contactos viejos",
    body: "Volvemos a contactar la base de clientes que quedó olvidada.",
  },
  {
    title: "Scorecard semanal",
    body: "Leads recibidos, respondidos, tiempo de respuesta, citas y sin seguimiento.",
  },
  {
    title: "Ajuste operativo",
    body: "No solo conectamos el software: entrenamos a tu equipo para que lo use.",
  },
];

export const differentiators: string[] = [
  "No vendemos likes",
  "Vemos tu operación real antes de recomendar nada",
  "Un tablero que el dueño entiende en 2 minutos",
  "No reemplazamos a tu equipo, le damos estructura",
  "No prometemos magia: primero cerramos la fuga",
];

export const forYou: string[] = [
  "Ya recibes leads por anuncios, llamadas, formularios, WhatsApp o redes",
  "Tienes a alguien (recepción, equipo o tú) que atiende prospectos",
  "Sientes que algunas oportunidades se pierden por falta de seguimiento",
  "No tienes visibilidad clara de qué pasó con cada lead",
];

export const notForYou: string[] = [
  "Todavía no recibes leads",
  "No hay nadie que pueda responder prospectos",
  "Solo buscas likes o seguidores",
  "Esperas ventas garantizadas sin ajustar el proceso",
];

export const leakSignals: string[] = [
  "No sabes cuántos leads quedaron sin respuesta",
  "Tu equipo responde desde teléfonos personales",
  "Si el prospecto no contesta una vez, nadie insiste",
  "No sabes cuánto tarda tu equipo en responder",
  "Tienes contactos viejos que nadie volvió a trabajar",
  "Tienes CRM pero nadie lo usa de forma consistente",
];

export const LEAK_THRESHOLD = 3;

export const diagnosisChecklist: string[] = [
  "De dónde llegan tus leads",
  "Quién los responde y cuánto tarda",
  "Qué pasa si el prospecto no contesta",
  "Si hay seguimiento o todo queda a memoria",
  "Si tienes pipeline o mensajes dispersos",
  "Qué oportunidades se están perdiendo",
];

export const faqs: { question: string; answer: string }[] = [
  {
    question: "¿Cuánto cuesta?",
    answer: "Depende de tu negocio. Lo definimos después del diagnóstico, sin compromiso.",
  },
  {
    question: "¿Esto es una agencia de marketing?",
    answer:
      "No exactamente. No empezamos vendiendo anuncios: trabajamos lo que pasa después de que entra el lead — respuesta, seguimiento, CRM y visibilidad.",
  },
  {
    question: "Ya tengo una agencia.",
    answer:
      "Perfecto. No competimos con tus anuncios: arreglamos lo que pasa después de que el lead entra.",
  },
  {
    question: "Mi equipo no va a usar un sistema nuevo.",
    answer: "Por eso vamos en persona y lo ajustamos a cómo ya trabajan.",
  },
  {
    question: "¿Necesito cambiar de CRM?",
    answer:
      "No necesariamente. Primero revisamos qué usas hoy. Si se puede ordenar con tus herramientas, lo hacemos.",
  },
  {
    question: "No tengo tiempo.",
    answer: "La llamada son 15 minutos y la visita la coordinamos a tu horario.",
  },
  {
    question: "¿Qué pasa en la visita?",
    answer:
      "Vemos cómo entran y se atienden tus leads. Si no somos la solución, te lo decimos.",
  },
];
